import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { PageTitle } from '../../../../_metronic/layout/core'
import { Tr } from '../../../../_metronic/i18n/Tr'
import { fetchData } from '../../../../reusable'
import AddListingAds from './AddListingAds'
import AddPromotion from './AddPromotion'
import AddPayment from './AddPayment'
import { AddAdvertisementHeader } from './AddSponsuredAdvertisementHeader'

interface SponserTarget {
  sponsertargetId: number
  sponsertargetmasterId: number
  sponserId: number
  value: (string | number)[]
  isActive: boolean
}

interface SponserDetail {
  sponserId: number
  vendorId: number
  status: string
  actionType: string
  actionTypeId: number | number[]
  targets: SponserTarget[]
  completeSteps: { action?: boolean; target?: boolean }
  sponserSetting: any
  promotionDetail: any[]
}

const AddSponsuredAds: React.FC = () => {
  const t = Tr()
  const location = useLocation()
  const navigate = useNavigate()

  const locationState = location.state as { adsData?: any; adsId?: string | number; tab?: number; isNew?: boolean; paymentFailed?: boolean } | null
  const [activeTab, setActiveTab] = useState(0)
  const [sponserDetail, setSponserDetail] = useState<SponserDetail | null>(null)

  const vendorData = sessionStorage.getItem('vendor')
  const vendorId = vendorData ? String(JSON.parse(vendorData).vendorId || '') : ''
  const [currentSponserId, setCurrentSponserId] = useState(
    locationState?.adsId ? String(locationState.adsId) : ''
  )

  const getVendorSponserDetail = async (overrideSponserId?: string) => {
    const idToUse = overrideSponserId || currentSponserId
    if (!idToUse || !vendorId) return
    const res: any = await fetchData('public/getvendorsponserdetail', { sponserId: idToUse, vendorId })
    if (String(res?.status) === '200' && res?.data) {
      const detail: SponserDetail = res.data
      setSponserDetail(detail)
      // Inject adsData into location state so AddListingAds can restore its selection
      if (detail.actionType && detail.actionTypeId != null) {
        navigate(location.pathname, {
          replace: true,
          state: {
            ...locationState,
            adsId: idToUse,
            adsData: {
              actionType: detail.actionType,
              actionTypeId: detail.actionTypeId,
              sponserId: detail.sponserId,
            },
          },
        })
      }
    }
  }

  useEffect(() => {
    getVendorSponserDetail()
  }, [])

  // Restore tab from location state on initial mount only
  useEffect(() => {
    if (locationState?.tab !== undefined) {
      setActiveTab(locationState.tab)
    }
  }, [])

  // Map API completeSteps (action/target) → header format (Listing/Detail/Budget/Pay)
  const completeSteps = sponserDetail?.completeSteps
    ? {
        Listing: sponserDetail.completeSteps.action ?? false,
        Detail: sponserDetail.completeSteps.target ?? false,
        Budget: false,
        Pay: false,
      }
    : undefined

  const changeTab = (tab: number) => {
    setActiveTab(tab)
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return (
          <AddListingAds
            onSaveSuccess={(newSponserId) => {
              if (newSponserId) setCurrentSponserId(newSponserId)
              setActiveTab(1)
              getVendorSponserDetail(newSponserId)
            }}
          />
        )

      case 1:
        return (
          <AddPromotion
            onSaveSuccess={() => {
              setActiveTab(2)
              getVendorSponserDetail()
            }}
            existingTargets={sponserDetail?.targets ?? []}
            sponserIdProp={currentSponserId || (locationState?.adsId ? String(locationState.adsId) : undefined)}
          />
        )

      case 2:
        return (
          <AddPayment
            amountPerDay={Number(sponserDetail?.sponserSetting?.amountPerDay || 0)}
            onPrevious={() => setActiveTab(1)}
          />
        )

      default:
        return null
    }
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>
        {t('Add Search Ads')}
      </PageTitle>
      <AddAdvertisementHeader
        activeTab={activeTab}
        onTabChange={changeTab}
        completeSteps={completeSteps}
      />
      <div className='d-flex flex-column flex-column-fluid'>
        <div id='kt_app_content' className='app-content flex-column-fluid'>
          <div className='form d-flex flex-column flex-lg-row'>
            <div className='d-flex flex-column flex-row-fluid gap-7 gap-lg-10'>
              <div className='tab-content'>
                <div
                  className={`tab-pane fade show active`}
                  id={`kt_advertisement_tab_${activeTab}`}
                  role='tab-panel'
                >
                  <div className='d-flex flex-column'>
                    {renderTabContent()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default AddSponsuredAds
